import type { GradingScale } from "@/data/gradingScales";

export interface SubjectRow {
  id: string;
  name: string;
  letter: string;
  credit: number;
}

export interface SemesterRow {
  id: string;
  name: string;
  gpa: number;
  credits: number;
}

export interface SemesterResult {
  ok: true;
  gpa: number;
  totalCredits: number;
  totalPoints: number;
  perRow: { row: SubjectRow; point: number; contribution: number }[];
}

export interface CumulativeResult {
  ok: true;
  cgpa: number;
  totalCredits: number;
}

export interface PredictorResult {
  ok: true;
  required: number;
  achievable: boolean;
}

export type CalcError =
  | { ok: false; error: "no_subjects" }
  | { ok: false; error: "zero_credits" }
  | { ok: false; error: "invalid_credit" }
  | { ok: false; error: "invalid_grade" };

export function calcSemesterGPA(
  rows: SubjectRow[],
  scale: GradingScale,
): SemesterResult | CalcError {
  if (rows.length === 0) return { ok: false, error: "no_subjects" };

  let sumPoints = 0;
  let sumCredits = 0;
  const perRow: SemesterResult["perRow"] = [];

  for (const r of rows) {
    if (!Number.isFinite(r.credit) || r.credit < 0 || r.credit > 6) {
      return { ok: false, error: "invalid_credit" };
    }
    const g = scale.grades.find((x) => x.letter === r.letter);
    if (!g) return { ok: false, error: "invalid_grade" };
    const contribution = g.point * r.credit;
    sumPoints += contribution;
    sumCredits += r.credit;
    perRow.push({ row: r, point: g.point, contribution });
  }

  if (sumCredits <= 0) return { ok: false, error: "zero_credits" };

  return {
    ok: true,
    gpa: sumPoints / sumCredits,
    totalCredits: sumCredits,
    totalPoints: sumPoints,
    perRow,
  };
}

export function calcCumulative(rows: SemesterRow[]): CumulativeResult | CalcError {
  if (rows.length === 0) return { ok: false, error: "no_subjects" };
  let sumWeighted = 0;
  let sumCredits = 0;
  for (const r of rows) {
    if (!Number.isFinite(r.credits) || r.credits < 0) {
      return { ok: false, error: "invalid_credit" };
    }
    sumWeighted += r.gpa * r.credits;
    sumCredits += r.credits;
  }
  if (sumCredits <= 0) return { ok: false, error: "zero_credits" };
  return { ok: true, cgpa: sumWeighted / sumCredits, totalCredits: sumCredits };
}

export function predictRequired(args: {
  currentCgpa: number;
  currentCredits: number;
  targetCgpa: number;
  remainingCredits: number;
  maxScale: number;
}): PredictorResult | CalcError {
  const { currentCgpa, currentCredits, targetCgpa, remainingCredits, maxScale } = args;
  if (remainingCredits <= 0 || currentCredits < 0) {
    return { ok: false, error: "zero_credits" };
  }
  const totalCredits = currentCredits + remainingCredits;
  const required =
    (targetCgpa * totalCredits - currentCgpa * currentCredits) / remainingCredits;
  return {
    ok: true,
    required,
    achievable: required <= maxScale + 1e-9 && required >= 0,
  };
}
